import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { filter, take } from 'rxjs/operators';
import { StorageUsage } from './dashboard.service';
import { StorageUsageService } from './storage-usage.service';
import { StorageLimitAlertComponent } from '../../modules/admin/dashboard/dialogs/storage-limit-alert/storage-limit-alert.component';

/**
 * Módulos cuja ENTRADA dispara a checagem do alerta de armazenamento.
 *
 * Módulos que fazem UPLOAD ou gravam arquivos no storage do tenant
 * (mapeados pelo código: componentes de upload/gerador de arquivos),
 * considerando as rotas reais do app (app.routing.ts):
 *
 * | prefixo da rota | módulo | evidência de gravação no storage (código) |
 * |---|---|---|
 * | `/financial/receipts` | Comprovantes | batch/unified — upload/lote de comprovantes |
 * | `/financial/entries` | Entradas | dialog entry (uploadReceiptEntry), cults (comprovante), lote |
 * | `/financial/exits` | Saídas | view-edit (comprovante), purchases (comprovante) |
 * | `/financial/accounts-cards` | Contas/Cartões | aba files → new-file (anexo de conta) |
 * | `/financial/accounting` | Contabilidade | bank-reconciliation → upload-statement (extrato) |
 * | `/secretary/membership` | Membros | new (foto de membro + documentos) |
 * | `/secretary/documents` | Documentos | minutes → upload-audio (transcrição), contracts → new |
 * | `/ecclesiastical/groups` | Grupos | add-initial-balance (comprovante de saldo) |
 * | `/users` | Usuários | dialog (avatar) |
 *
 * A lista é uma PROPOSTA para revisão — remover/ajustar prefixos conforme o
 * critério de produto (a ordem importa: prefixos mais específicos primeiro é
 * desnecessário aqui pois não há prefixos que sejam pré-requisito um do outro).
 */
export interface StorageAlertModule
{
    prefix: string;
    label: string;
}

export const STORAGE_ALERT_MODULES: StorageAlertModule[] = [
    { prefix: '/financial/receipts',      label: 'Comprovantes' },
    { prefix: '/financial/entries',       label: 'Entradas' },
    { prefix: '/financial/exits',         label: 'Saídas' },
    { prefix: '/financial/accounts-cards', label: 'Contas e cartões' },
    { prefix: '/financial/accounting',    label: 'Contabilidade' },
    { prefix: '/secretary/membership',    label: 'Membros' },
    { prefix: '/secretary/documents',     label: 'Documentos' },
    { prefix: '/ecclesiastical/groups',   label: 'Grupos' },
    { prefix: '/users',                   label: 'Usuários' },
];

/**
 * Alerta de limite de armazenamento (>= 80% da cota) — dirigido por GATILHOS
 * de uso (não mais "1x por sessão"):
 *
 *  1. Após cada LOGIN (logo após a busca única do snapshot);
 *  2. Após cada CADASTRO de membro (sucesso da criação);
 *  3. A cada ENTRADA em módulo que grava arquivos no storage (anti-spam:
 *     exibe ao ENTRAR no módulo vindo de fora; troca de aba interna não
 *     dispara; sair e re-entrar re-exibe);
 *
 * Critérios de exibição (mantidos): `used_percent >= 80` E `computed_at`
 * preenchido. Qualquer outro caso (erro/403, snapshot em cálculo, uso abaixo
 * do limite, payload ausente) é SILENCIOSO. Todas as checagens consultam a
 * versão em CACHE do snapshot (StorageUsageService) — NUNCA re-chamam o
 * endpoint.
 */
@Injectable({
    providedIn: 'root'
})
export class StorageLimitAlertService
{
    private _openDialogRef: MatDialogRef<StorageLimitAlertComponent> | null = null;
    private _lastModuleKey: string | null = null;

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _matDialog: MatDialog,
        private _storageUsageService: StorageUsageService
    )
    {
        // Listener PERMANENTE de módulos: ao ENTRAR em uma rota de módulo com
        // arquivos, dispara a checagem. Vive no serviço (singleton root) para
        // sobreviver à recriação do layout entre blocos de rota.
        this._router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event) => {
                this.checkOnNavigation((event as NavigationEnd).urlAfterRedirects);
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods (gatilhos)
    // -----------------------------------------------------------------------------------------------------

    /**
     * Gatilho 1 — após cada login bem-sucedido (logo após a busca única).
     */
    checkOnLogin(): void
    {
        this._storageUsageService.load();
        this._checkWhenReady();
    }

    /**
     * Gatilho 2 — após cada cadastro (criação) de membro com sucesso.
     */
    checkOnMemberCreated(): void
    {
        this._storageUsageService.load();
        this._checkWhenReady();
    }

    /**
     * Gatilho 3 — a cada ENTRADA em módulo que grava arquivos no storage.
     *
     * Anti-spam: exibe ao ENTRAR no módulo (vindo de fora dele); trocas de
     * aba/tela DENTRO do módulo não re-exibem; sair e re-entrar re-exibe.
     *
     * @param url URL após redirects de uma navegação concluída (NavigationEnd).
     */
    checkOnNavigation(url: string): void
    {
        const moduleKey = this._resolveModuleKey(url);

        // Fora de módulo mapeado → apenas registra a saída ("não estou num
        // módulo de arquivos") para o anti-spam funcionar na re-entrada.
        if ( !moduleKey )
        {
            this._lastModuleKey = null;
            return;
        }

        // Ainda dentro do MESMO módulo (troca de aba/rota interna) → skip.
        if ( moduleKey === this._lastModuleKey )
        {
            return;
        }

        this._lastModuleKey = moduleKey;

        this._storageUsageService.load();
        this._checkWhenReady();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Aguarda o snapshot (em cache) e avalia os critérios do alerta.
     * Se a busca única ainda estiver em voo (1ª vez da sessão), registra uma
     * pendência única que avalia assim que o snapshot resolver.
     *
     * @private
     */
    private _checkWhenReady(): void
    {
        if ( this._storageUsageService.loaded )
        {
            this._evaluate(this._storageUsageService.usage);
            return;
        }

        this._storageUsageService.resolved$
            .pipe(take(1))
            .subscribe(() => this._evaluate(this._storageUsageService.usage));
    }

    /**
     * Avalia os critérios do alerta contra o snapshot em cache:
     * `used_percent >= 80` E `computed_at` preenchido. Dispara o dialog
     * dedicado `storage-limit-alert` (tema warning âmbar, botão "Entendi").
     * Anti-empilhamento: com o dialog já aberto, não abre outro.
     *
     * @private
     */
    private _evaluate(usage: StorageUsage | null): void
    {
        // Silencioso: snapshot ausente/erro/403 ou ainda em cálculo.
        if ( !usage || !usage.computed_at )
        {
            return;
        }

        const percent = Number(usage.used_percent) || 0;

        // Silencioso: abaixo do limite (80%).
        if ( percent < 80 )
        {
            return;
        }

        // Anti-empilhamento: dialog já aberto → não abre outro.
        if ( this._openDialogRef )
        {
            return;
        }

        this._openDialogRef = this._matDialog.open(StorageLimitAlertComponent, {
            autoFocus: false,
            disableClose: false,
            width: '95%',
            maxWidth: '640px',
            maxHeight: '90vh',
            panelClass: 'custom-dialog-container',
            data: {
                usedPercent: percent,
                planStorageGb: Number(usage.plan_storage_gb) || 0
            }
        });

        this._openDialogRef.afterClosed().subscribe(() => {
            this._openDialogRef = null;
        });
    }

    /**
     * Resolve o prefixo de módulo da URL (sem query string) contra a lista
     * mapeada. Retorna null quando a URL não pertence a módulo com arquivos.
     *
     * @private
     */
    private _resolveModuleKey(url: string): string | null
    {
        const path = url.split('?')[0];

        for ( const module of STORAGE_ALERT_MODULES )
        {
            if ( path.startsWith(module.prefix) )
            {
                return module.prefix;
            }
        }

        return null;
    }
}